/**
 * Dates for the mental-health follow-up, expressed the way the backend reads them.
 *
 * The backend stores followUpDueAt as an instant and then derives the reminder in Europe/London.
 * Formatting that instant with `.slice(0, 10)` reads its UTC date instead, which is a different
 * calendar day for anything between 23:00Z and midnight during BST - so a follow-up chosen late in
 * the evening would display, and on re-save become, a day earlier than the clinician chose.
 *
 * Everything here therefore goes through Europe/London, and every date sent to the backend is noon
 * UTC of the chosen day. Noon is deliberate: the UK is never more than an hour ahead of UTC and
 * never behind it, so noon UTC is the same calendar day in London in both BST and GMT. Midnight
 * would sit on the boundary and is one DST change away from being wrong.
 */
const UK_TIMEZONE = 'Europe/London';

/**
 * The Europe/London calendar day of an instant, as YYYY-MM-DD (the format <input type="date"> wants).
 *
 * @param {string|number|Date} value
 * @returns {string} '' when the value is missing or unparseable
 */
export function toUkDateString(value) {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
        return '';
    }

    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: UK_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);

    const partOfType = type => {
        const found = parts.find(part => part.type === type);

        return found ? found.value : '';
    };

    return `${partOfType('year')}-${partOfType('month')}-${partOfType('day')}`;
}

/**
 * Today's date in the UK, as YYYY-MM-DD.
 *
 * @returns {string}
 */
export function ukToday() {
    return toUkDateString(new Date());
}

/**
 * A UK calendar date a number of days from another one, as YYYY-MM-DD.
 *
 * @param {number} days
 * @param {string} [from] YYYY-MM-DD; defaults to today in the UK
 * @returns {string}
 */
export function addUkDays(days, from) {
    const base = new Date(`${from || ukToday()}T12:00:00.000Z`);

    base.setUTCDate(base.getUTCDate() + days);

    return toUkDateString(base);
}

/**
 * A UK calendar date as clinicians read it: DD-MM-YYYY.
 *
 * Display only. Everything that is stored, compared or handed to <input type="date"> stays in
 * YYYY-MM-DD - the date input accepts no other format, and the ordering comparisons that keep a
 * reminder out of the past rely on ISO strings sorting chronologically. DD-MM-YYYY does not.
 *
 * @param {string} ukDate YYYY-MM-DD
 * @returns {string} DD-MM-YYYY, or '' when there is no date
 */
export function displayUkDate(ukDate) {
    if (!ukDate) {
        return '';
    }

    const [year, month, day] = ukDate.split('-');

    return (year && month && day) ? `${day}-${month}-${year}` : '';
}

/**
 * An instant as the UK calendar date a clinician reads: DD-MM-YYYY.
 *
 * @param {string|number|Date} value
 * @returns {string} DD-MM-YYYY, or '' when the value is missing or unparseable
 */
export function toUkDisplayDate(value) {
    return displayUkDate(toUkDateString(value));
}

/**
 * A UK calendar date as the instant to send to the backend.
 *
 * @param {string} ukDate YYYY-MM-DD
 * @returns {string} an ISO instant, or '' when there is no date
 */
export function ukDateToInstant(ukDate) {
    return ukDate ? new Date(`${ukDate}T12:00:00.000Z`).toISOString() : '';
}
